import type { Messages } from '../messages.js'

/**
 * Romanian. `mutare` for flips, the word a Romanian board game uses for a move.
 *
 * Three plural forms, and the third is the one nothing else here has: from twenty upward
 * Romanian puts `de` between the number and the noun, so it is `19 cuvinte` but `20 de cuvinte`.
 * That is what `other` carries.
 */
export const ro: Messages = {
  tag: 'ro',

  readingDictionary: 'Se citește dicționarul…',
  noWordList: 'Nu există listă de cuvinte pentru „{language}”. Creați una:  pnpm dictionary build',
  emptyWordList: 'Lista de cuvinte pentru „{language}” este goală.',

  flips: 'mutări',
  score: 'scor',
  words: 'cuvinte',
  round: 'rundă',
  ticksLeftLabel: 'Timp rămas în această rundă',
  typeAWord: 'scrieți un cuvânt',
  tapPrompt: 'atingeți literele pentru a le alege sau returna, apoi {action}',

  boardOfTiles: 'Tablă cu {n} piese',
  faceDown: 'cu fața în jos',
  wildCard: 'joker',
  wildKey: 'orice literă',
  letterReplaced: '{from} a devenit {to}',
  letterSwap: 'SCHIMB DE LITERE!',
  spentTile: 'piesă folosită',
  hiddenWhilePaused: 'ascuns pe durata pauzei',
  letterInWord: '{letter}, litera {position} a cuvântului',

  completeWord: 'Termină cuvântul',

  completeShort: 'Gata',
  reset: 'Șterge',
  pause: 'Pauză',
  resume: 'Reia',
  newGame: 'Joc nou',
  paused: 'În pauză',
  outOfFlips: 'S-au terminat mutările',
  finalResult: '{score} pentru {words} în {rounds}',
  playAgain: 'Mai joacă o dată',
  share: 'Distribuie',
  shareCopied: 'Copiat.',
  shareSelect: 'Copiați asta:',

  lettersSelect: 'literele aleg',
  keysWild: 'se ia când scrieți o literă pe care nicio piesă nu o arată',
  clearsEvery: 'șterge toate literele {letter} alese',
  undoLastLetter: 'anulează ultima literă',
  noWordsYet: 'Niciun cuvânt încă.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'deja găsit',
  reasonTooShort: 'prea scurt',
  reasonNotAWord: 'nu este un cuvânt',
  reasonAllFound: 'le aveți deja pe toate',
  noSuchLetterUp: 'nicio literă {letter} întoarsă',
  nothingUp: 'nimic întors',
  letterHidden: 'o literă s-a ascuns',
  shuffled: 'amestecat',
  shuffledAndBilled: 'amestecat, s-au scăzut {flips} nefolosite',
  tooFewLetters: 'Au rămas prea puține litere',

  gameLanguage: 'limbă',
  interfaceLanguage: 'interfață',
  dictionarySize: '{common} obișnuite din {full} de cuvinte',
  filterLanguages: 'Caută o limbă',
  noMatches: 'Nicio potrivire',

  nerdMode: 'mod pentru pasionați',
  rules: 'Reguli',
  difficulty: 'dificultate',
  difficultyNames: { easy: 'ușoară', medium: 'medie', hard: 'grea', insane: 'nebunească' },
  pausedNoBoard: 'Acest joc nu va apărea în clasament.',
  about: 'Despre',
  themeLabel: 'temă',
  themeNames: { traditional: 'tradițională', light: 'luminoasă', contrast: 'contrast ridicat' },
  tiles: 'piese (N)',
  secondsPerTick: 'secunde / bătaie',
  holdTicks: 'bătăi de așteptare',
  minWord: 'cel mai scurt cuvânt',
  startingFlips: 'mutări la început',
  wildChance: 'șansa de joker',
  replaceChance: 'șansa de schimb de literă',
  hideChance: 'șansa de ascundere a unei litere',
  wordCompleteMode: 'când cuvântul e gata',
  wordCompleteNames: { shuffle: 'amestecă', spend: 'consumă', keep: 'păstrează' },
  flipEconomy: 'returnarea mutărilor',
  flipEconomyNames: {
    none: 'niciuna',
    perLetter: 'per literă',
    fibonacci: 'fibonacci',
    overMinimum: 'peste minim',
  },
  repeatedLetterKey: 'tasta literei repetate',
  keySchemeNames: { cycle: 'ciclu', advance: 'înainte' },
  keySchemeHelp: {
    cycle:
      'A ia următorul A nefolosit, iar când toate sunt în cuvânt, le șterge. ' +
      'Shift+A le șterge de asemenea.',
    advance: 'A ia următorul A nefolosit. Shift+A șterge toate literele A din cuvânt.',
  },

  whatThatMeans: 'Ce înseamnă asta',
  factRound: 'rundă',
  factWholeBoardUp: 'toată tabla întoarsă timp de',
  factRoundCosts: 'o rundă costă',
  factFlipsBuy: 'mutările de la început cumpără',
  factThisBoard: 'această tablă',
  factBoardHadToAdmit: 'tabla a trebuit să admită',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, cel mai lung {longest}',
  wordsIncludingOneOf: '{words}, dintre care unul de {ceiling}',
  scorelessRounds: '{rounds} fără puncte',

  whatAWordPays: 'Cât aduce un cuvânt',
  columnLetters: 'litere',
  columnCost: 'cost',
  columnPoints: 'puncte',
  columnFlips: 'mutări',
  columnNet: 'net',

  canonicalRules: 'Regulile obișnuite pentru {difficulty}.',
  customRules: 'Modificat față de presetare. Scorurile cu reguli proprii nu intră în clasament.',
  applyAndStart: 'Aplică și începe un joc nou',
  changesNextGame: 'Modificările se aplică de la jocul următor.',
  presets: 'Presetări:',

  start: 'Începe',
  restart: 'De la capăt',
  quit: 'Renunță',
  quitTitle: 'Renunțați la acest joc?',
  restartTitle: 'Reluați acest joc de la capăt?',
  restartConfirm: 'De la capăt',
  quitConfirm: 'Renunță',
  keepPlaying: 'Continuă jocul',
  personalBest: 'Cele mai bune jocuri ale dumneavoastră',
  newPersonalBest: 'Un nou record personal.',
  columnRank: '#',
  notRanked: 'Reguli proprii, deci acest joc nu intră în clasament.',
  notRankedPaused: 'Ceasul s-a oprit, deci acest joc nu intră în clasament.',
  rankOfTotal: '{rank} din {total}',

  howToPlay: 'Cum se joacă',

  backToGame: 'Înapoi la joc',
  welcomeTitle: 'Bun venit la Blinkered',
  tutorialSkip: 'Sari peste',
  tutorialNext: 'Înainte',
  tutorialBack: 'Înapoi',
  tutorialStart: 'Începe să joci',
  tutorialHideAgain: 'Nu mai arăta asta',
  tutorialProgress: '{n} din {total}',
  tutorialSkipTitle: 'Săriți peste prezentare?',
  tutGoalTitle: 'Blinkered este o cursă',
  tutGoalBody:
    'Începeți jocul cu un număr fix de mutări, iar fiecare piesă întoarsă costă o mutare. Când mutările se termină, jocul s-a încheiat. Cuvintele aduc mutări, iar cu cât cuvântul e mai lung, cu atât câștigați mai multe mutări.',
  tutSpentBody:
    'Literele folosite într-un cuvânt rămân cu fața în jos până în runda următoare. Doar schimbarea unei litere modifică literele de pe tablă.',
  tutChangeTitle: 'Literele se pot schimba',
  tutSameLetters:
    'Ați văzut un cuvânt pe care n-ați apucat să-l jucați? Aceleași litere revin în runda următoare, amestecate în locuri noi.',
  tutPickLetters: 'Atingeți literele dorite, în ordine, pentru a forma un cuvânt.',
  tutMoreTurn:
    'Piesele se întorc și în timp ce gândiți, așa că o literă mai bună poate încă să vină.',
  tutTapBack:
    'Ați luat una pe care nu o voiați? Atingeți-o din nou și se întoarce. Oricare, nu doar ultima.',
  tutComplete: 'Apăsați Gata când cuvântul e pregătit.',
  htBoardTitle: 'Tabla',
  htBoardBody:
    'Piesele se întorc una câte una, într-o ordine aleatorie. Din cele întoarse se formează cuvinte.',
  htWordsTitle: 'Cuvintele',
  htWordsBody: 'Formați un cuvânt din piesele întoarse scriind sau apăsând literele în ordine.',
  htFlipsTitle: 'Mutările',
  htFlipsBody:
    'Fiecare piesă întoarsă costă o mutare. Un cuvânt terminat returnează mutări, iar cuvintele lungi returnează mai multe. Când mutările se termină, jocul s-a încheiat.',
  htRoundTitle: 'Runda',
  htRoundBody:
    'Când se întoarce ultima piesă a rundei, toată tabla stă o clipă. Apoi piesele sunt întoarse pe dos și amestecate, și începe o rundă nouă.',
  htLanguagesTitle: 'Limbile',
  htLanguagesBody:
    'Sunt {n}. Orice tablă poate fi rezolvată cu cuvinte pe care oamenii chiar le folosesc. Un cuvânt rar aduce și el puncte, dacă dicționarul îl cunoaște.',
  htKeysTitle: 'Tastatura',
  htWildTitle: 'Jokerii',
  htWildBody:
    'Uneori apare un joker în loc de literă. Jokerul contează ca orice literă care formează un cuvânt valid. Un cuvânt deja găsit nu contează.',
  htHideTitle: 'Litere care se ascund',
  htHideBody:
    'La nivelurile mai grele o literă descoperită se poate întoarce singură și revine mai târziu în rundă. Cronometrul primește o diviziune în plus pentru asta, în roșu. Literele deja alese nu se întorc.',
  tutHideBody:
    'La nivelurile mai grele o literă descoperită se poate întoarce singură și revine mai târziu în rundă. Literele deja alese nu se întorc.',
  htSwapTitle: 'Litere care se schimbă',
  htSwapBody:
    'Uneori, între runde, o literă este înlocuită cu alta. Veți vedea care a plecat și care a venit.',
  htLevelsTitle: 'Nivelurile',
  htLevelEasy:
    'Aceleași douăsprezece litere tot jocul, așa că le puteți învăța și purta o listă de cuvinte în minte. Piesele se întorc încet, iar toată tabla rămâne vizibilă destul ca să terminați alegerea.',
  htLevelMedium:
    'Din când în când o literă se schimbă, deci e mai greu să țineți minte cuvintele păstrate pentru mai târziu. Mai puțin timp de privit și mai puțin de gândit.',
  htLevelHard:
    'Cuvintele de trei litere nu mai contează, iar o literă se schimbă cam la fiecare a doua rundă. Tabla abia apucă să se arate și deja se amestecă.',
  htLevelInsane:
    'Totul deodată, la viteză maximă. Tabla se amestecă aproape imediat după ultima mutare.',
  htTouchTitle: 'Ecranul tactil',
  htTouchBody:
    'Atingeți o piesă întoarsă ca să îi luați litera. Atingeți o literă luată ca să o returnați. Gata și Șterge sunt sub tablă.',

  accountTitle: 'Contul tău',
  menuProfile: 'Profilul meu',
  menuGames: 'Jocurile mele',
  menuPublicPage: 'Pagina mea publică',
  menuSignedInAs: 'Autentificat ca',
  signIn: 'Autentificare',
  signOut: 'Deconectare',
  signInTitle: 'Autentifică-te sau creează un cont',
  signInLead: 'Un cont îți păstrează jocurile, pe orice dispozitiv.',
  signInKeepGame: 'Autentifică-te ca să păstrezi acest joc, și pe toate următoarele.',
  continueWithApple: 'Continuă cu Apple',
  continueWithGoogle: 'Continuă cu Google',
  signInOr: 'sau',
  emailLabel: 'E-mail',
  codeLabel: 'Cod',
  emailMeACode: 'Trimiteți-mi un cod',
  working: 'O clipă…',
  sendNewCode: 'Trimite un cod nou',
  codeSent: 'Verifică-ți e-mailul. Codul durează 10 minute.',
  notNow: 'Nu acum',
  badEmail: 'Asta nu pare o adresă de e-mail.',
  badCode: 'Codul nu a funcționat. Codurile expiră după 10 minute și se folosesc o dată.',
  serverBusy: 'Serverul nu a putut fi contactat. Încearcă peste un moment.',
  serverDown: 'Serverul nu a putut fi contactat.',
  offline: 'Deconectat',
  leaderboardTitle: 'Clasament',
  leaderboardEmpty: 'Încă nu există jocuri în acest clasament. Fii primul.',
  leaderboardWouldBe: 'Unde s-ar clasa acest joc',
  leaderboardThisGame: 'Acest joc',
  leaderboardRanksHere: 'Unde se clasează acest joc',
  leaderboardOutside: 'În afara primelor cinci.',
  tutAccountTitle: 'Păstrează-ți scorurile',
  tutAccountBody:
    'Un cont salvează jocurile tale, pune scorurile în clasament și păstrează profilul tău.',
  ssoCancelled: 'Autentificarea a fost anulată.',
  ssoExpired: 'A durat prea mult. Autentifică-te din nou.',
  ssoNoUsername: 'Contul nu a putut fi finalizat. Încearcă peste un moment.',
  ssoFailed: 'Nu a mers. Încearcă din nou, sau folosește e-mailul.',
  tabProfile: 'Profil',
  countryAny: 'Prefer să nu spun',
  findCountry: 'Caută țări',
  usernameLabel: 'Nume de utilizator',
  bioLabel: 'Despre mine',
  countryLabel: 'Țară',
  bioHint: 'Fără linkuri. {left}/{max}',
  save: 'Salvează',
  saving: 'Se salvează…',
  saved: 'Salvat.',
  nameTaken: 'Numele e deja luat.',
  nameTooShort: 'Puțin mai lung, te rog.',
  nameTooLong: 'E prea lung.',
  nameBadCharacters: 'Doar litere, cifre, cratime și liniuțe de subliniere.',
  nameBadEdges: 'Trebuie să înceapă și să se termine cu o literă sau o cifră.',
  nameMixedScripts: 'Alege un singur alfabet și rămâi la el.',
  nameReserved: 'Seamănă cu un nume pe care îl dăm noi. Alege altul.',
  nameUnusable: 'Numele nu poate fi folosit.',
  bioTooLong: 'Descrierea are cel mult {max} caractere.',
  bioHasLink: 'Linkurile nu sunt permise în descriere.',
  bioHasControl: 'Conține caractere pe care descrierea nu le poate avea.',
  bioUnusable: 'Descrierea nu poate fi folosită.',
  gamesHeading: 'Jocuri',
  gamesEmpty: 'Încă nu e nimic aici. Jocurile pe care le joci autentificat apar aici.',
  gamesEmptyShort: 'Încă nu e nimic aici.',
  gamesLoading: 'Se citesc jocurile tale…',
  theirGamesLoading: 'Se citesc jocurile lor…',
  profileLoading: 'Se citește profilul…',
  playerNotFound: 'Nu e nimeni aici cu numele ăsta.',
  columnWhen: 'Când',
  columnRounds: 'de runde',
  columnGame: 'Joc',
  gameLoading: 'Se citește jocul…',
  gameUnreadable: 'Jocul nu a putut fi citit.',
  gameNotRanked: 'Jucat cu reguli modificate, așa că nu intră în clasament.',
  detailNotKept: 'Cuvintele și tablele acestui joc nu mai sunt păstrate.',
  openingBoard: 'Tabla de start',
  roundByRound: 'Rundă cu rundă',
  roundNothing: 'nimic găsit',
  changeReplaced: '{letter} înlocuit în locul {slot}',
  changeWildOn: '{letter} joker în locul {slot}',
  changeWildOff: '{letter} joker luat din locul {slot}',
  keepThisGame: 'Păstrează jocul',
  savedToYourGames: 'Salvat în jocurile tale.',

  reportThis: 'Raportează',
  reportHeading: 'Raportează asta',
  reportTheName: 'Numele',
  reportTheBio: 'Descrierea',
  reportTheScore: 'Scorul',
  reportWhy: 'Ce e greșit la el? Opțional.',
  reportSend: 'Trimite raportul',
  reportSent: 'Mulțumim. Cineva se va uita.',
  reportSignIn: 'Conectează-te ca să raportezi ceva.',
  reportGone: 'Asta nu mai există.',
  reportCancel: 'Anulează',

  deleteAccount: 'Șterge contul meu',
  deleteAccountWhat:
    'Asta elimină profilul tău și fiecare joc pe care l-ai păstrat, definitiv. Nu se poate anula.',
  deleteAccountAsk: 'Trimite-mi un cod',
  deleteConfirm: 'Șterge definitiv',
  deleteGone: 'Contul tău nu mai există. Mulțumim că ai jucat.',
  deleteNoAddress:
    'Nu există o adresă confirmată pe acest cont, deci o ștergere nu poate fi verificată. Scrie-ne și o facem manual.',

  rulesetFacts: 'Cuvinte de {letters} · o piesă nouă la fiecare {seconds}',
  plurals: {
    words: { one: '{n} cuvânt', few: '{n} cuvinte', other: '{n} de cuvinte' },
    rounds: { one: '{n} rundă', few: '{n} runde', other: '{n} de runde' },
    flips: { one: '{n} mutare', few: '{n} mutări', other: '{n} de mutări' },
    ticks: { one: '{n} bătaie', few: '{n} bătăi', other: '{n} de bătăi' },
    points: { one: '{n} punct', few: '{n} puncte', other: '{n} de puncte' },
    minLetters: {
      one: 'cel puțin {n} literă',
      few: 'cel puțin {n} litere',
      other: 'cel puțin {n} de litere',
    },
    seconds: { one: '{n} secundă', few: '{n} secunde', other: '{n} de secunde' },
  },
}
